import { useState } from "react";
import { Bot, Check, Copy } from "lucide-react";
import type { ResourceDraft, ResourceWizardApi } from "../ResourceWizard";
import { InstructionSteps } from "./WizardUi";

type Props = {
  api: ResourceWizardApi;
  draft: ResourceDraft;
  onBack: () => void;
  onNext: () => void;
};

const SYSTEM_BOT_USERNAME = "@system_checking_bot";

const VERIFY_ERROR_MESSAGES: Record<string, string> = {
  resource_not_found: "ресурс не найден",
  resource_inactive: "ресурс временно недоступен",
  verification_not_supported: "проверка для этого ресурса не поддерживается",
  system_bot_missing: "системный бот не добавлен в ресурс",
  system_bot_not_added_to_chat:
    "системный бот не добавлен администратором в этот канал/чат",
  system_bot_not_admin: "системный бот должен быть администратором",
  system_bot_missing_rights: "у системного бота недостаточно прав",
  system_bot_rights_missing: "у системного бота недостаточно прав",
  telegram_error: "не удалось получить данные Telegram",
  telegram_transport_error: "не удалось получить данные Telegram",
  transport_error: "не удалось проверить ресурс",
};

export function Step4Verify({ api, draft, onNext }: Props) {
  const [status, setStatus] = useState<"idle" | "running" | "ok" | "error">(
    draft.system_bot_verified_at ? "ok" : "idle",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function copySystemBotUsername() {
    try {
      await navigator.clipboard.writeText(SYSTEM_BOT_USERNAME);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }

  async function runVerify() {
    if (!draft.id) return;
    setStatus("running");
    setErrorMessage(null);
    try {
      const r = await api.verifyResource(draft.id);
      if (r.error) {
        setStatus("error");
        setErrorMessage(formatApiError(r.error));
        return;
      }
      setStatus("ok");
    } catch (e) {
      setStatus("error");
      setErrorMessage(String(e));
    }
  }

  return (
    <div className="tp-resource-wizard-step space-y-3">
      <div className="tp-resource-system-bot-card">
        <Bot className="tp-resource-system-bot-icon" aria-hidden="true" />
        <span className="min-w-0">
          <span className="tp-resource-system-bot-name">
            {SYSTEM_BOT_USERNAME}
          </span>
          <span className="tp-resource-system-bot-note">
            для проверки вступлений и отписок
          </span>
        </span>
        <button
          type="button"
          className="tp-resource-system-bot-copy"
          aria-label={
            copied
              ? "username системного бота скопирован"
              : "скопировать username системного бота"
          }
          onClick={() => void copySystemBotUsername()}
        >
          {copied ? (
            <Check aria-hidden="true" />
          ) : (
            <Copy aria-hidden="true" />
          )}
        </button>
      </div>

      <InstructionSteps
        steps={[
          { text: "добавьте бота в администраторы" },
          {
            text: (
              <>
                <span>выдайте права:</span>
                <span className="tp-resource-instruction-subline">
                  • добавление подписчиков
                </span>
              </>
            ),
          },
          {
            text: (
              <>
                <span>нажмите </span>
                <button
                  type="button"
                  className="tp-resource-inline-action"
                  onClick={runVerify}
                  disabled={status === "running"}
                >
                  {status === "running" ? "проверяю..." : "проверить"}
                </button>
              </>
            ),
          },
        ]}
      />

      {status === "ok" && (
        <p className="tp-resource-check-status text-sm text-green-600">
          успешно!
        </p>
      )}
      {status === "error" && (
        <p className="text-sm text-red-600">{errorMessage}</p>
      )}
      <button
        onClick={onNext}
        disabled={status !== "ok"}
        className="tp-button-primary w-full disabled:opacity-50"
      >
        далее
      </button>
    </div>
  );
}

function formatApiError(error: unknown): string {
  if (!error) return "не удалось проверить ресурс";
  if (typeof error === "string") return normalizeVerifyError(error);
  try {
    const anyError = error as {
      error?: { message?: string; code?: string };
      detail?: string;
    };
    const message = anyError.error?.message;
    if (typeof message === "string" && message.trim()) {
      return stripTrailingPunctuation(message);
    }
    const code = anyError.error?.code;
    if (code && VERIFY_ERROR_MESSAGES[code]) return VERIFY_ERROR_MESSAGES[code];
    const detail = anyError.detail;
    if (typeof detail === "string" && detail.trim()) {
      return normalizeVerifyError(detail);
    }
    return "не удалось проверить ресурс";
  } catch {
    return "не удалось проверить ресурс";
  }
}

function normalizeVerifyError(value: string): string {
  const trimmed = value.trim();
  const mapped = VERIFY_ERROR_MESSAGES[trimmed];
  if (mapped) return mapped;
  const withoutTrailingPunctuation = trimmed.replace(/[.!?]+$/g, "");
  if (/[a-z]/i.test(withoutTrailingPunctuation)) {
    return "не удалось проверить ресурс";
  }
  return withoutTrailingPunctuation;
}

function stripTrailingPunctuation(value: string): string {
  return value.trim().replace(/[.!?]+$/g, "");
}
